import React from 'react';
import {
    Dialog,
    IconButton,
    Typography,
    DialogTitle,
    DialogContent,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {HotkeyConfiguration} from '@accurate-player/accurate-player-plugins';
import {capitalize, ensureObject} from '@pluto/utils';
import {
    HotkeyGroup,
    GroupKeyType,
    HOTKEY_GROUPS,
    HotKeyGroupsList,
} from './HotkeyDialogTypes';
import HotkeySection from './HotkeySection/HotkeySection';
import './HotkeyDialog.scss';

export const formatKeyCombo = (keyCombo = '') =>
    keyCombo.split('+').map(capitalize).join(' + ').split(', ').map(capitalize);

export const reduceHotKeys = (keys: HotkeyConfiguration[]) =>
    keys.reduce((acc: HotKeyGroupsList, curr) => {
        return {
            ...acc,
            [curr.keyCombo]: curr,
        };
    }, {});

export const getGroupKeys = <T extends HotKeyGroupsList, U extends keyof T>(
    hotKeys: T
) => (groupName: HotkeyGroup, groups = HOTKEY_GROUPS) => {
    return (groups[groupName] as GroupKeyType<U>[]).map(
        ({key, title}: GroupKeyType<U>) => {
            const {keyCombo, description} = ensureObject<T[U]>(hotKeys[key]);

            return {
                keyCombo: formatKeyCombo(keyCombo),
                description: title || description,
            };
        }
    );
};

export interface HotkeyDialogProps {
    hotkeys: HotkeyConfiguration[];
    onClose: () => void;
}

export default function HotkeyDialog({hotkeys, onClose}: HotkeyDialogProps) {
    const hotkeysList = reduceHotKeys(hotkeys);
    const getKeys = getGroupKeys<HotKeyGroupsList, keyof HotKeyGroupsList>(hotkeysList);

    return (
        <Dialog
            open
            fullWidth
            maxWidth="lg"
            onClose={onClose}
            aria-labelledby="hotkey-dialog-title"
            aria-describedby="hotkey-dialog-description"
            className="hotkey-dialog"
        >
            <DialogTitle disableTypography className="hotkey-dialog-title">
                <Typography variant="h5">Application hotkeys</Typography>
                <IconButton aria-label="close" onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div className="hotkey-group">
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.Playback)}
                        section={HotkeyGroup.Playback}
                    />
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.Step)}
                        section={HotkeyGroup.Step}
                    />
                </div>
                <div className="hotkey-group">
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.Seek)}
                        section={HotkeyGroup.Seek}
                    />
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.Volume)}
                        section={HotkeyGroup.Volume}
                    />
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.AddItem)}
                        section={HotkeyGroup.AddItem}
                    />
                </div>
                <div className="hotkey-group">
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.EditSelected)}
                        section={HotkeyGroup.EditSelected}
                    />
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.SelectRange)}
                        section={HotkeyGroup.SelectRange}
                    />
                    <HotkeySection
                        groupedHotkeys={getKeys(HotkeyGroup.Others)}
                        section={HotkeyGroup.Others}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
